import { useQuery } from "@tanstack/react-query";
import { Building2, Check, ChevronDown, MapPin } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import type { VisibleLocation, VisibleLocationsResponse } from "@tilt/pos-sdk";

import { getPosClient } from "../../lib/api";
import { cn } from "../../lib/utils";
import { useFocusTrap } from "../../lib/use-focus-trap";

export interface LocationSubsetPickerProps {
  /** Selected location IDs. Empty array == "all visible locations"
   *  (consolidated). Org / partner / platform-scoped operators run a
   *  consolidated rollup by default; a non-empty subset narrows. */
  readonly value: ReadonlyArray<string>;
  readonly onChange: (next: ReadonlyArray<string>) => void;
  /** When ``true`` the picker stays mounted but disables every input -
   *  used while the parent is fetching the report so the operator can't
   *  click around mid-request. */
  readonly disabled?: boolean;
}

/** Consolidated-vs-subset location picker for org / partner / platform
 *  scoped reports / dashboards / transaction lists. Renders a labeled
 *  trigger button that summarises the current selection ("All N
 *  locations" / "1 location" / "3 locations"); clicking opens a
 *  popover with org-grouped checkboxes + a "Clear" button to reset
 *  to consolidated.
 *
 *  Empty selection = consolidated rollup (the most common case).
 *  Non-empty = the subset; the parent forwards as ``location_ids``
 *  to the backend. The backend treats a single-element subset as a
 *  legacy single-location filter so the same component works for
 *  partner / platform-scope users who happen to pick one store.
 *
 *  The component self-hides when the active membership is location-
 *  scoped (the lookups handler returns a single-row response and a
 *  picker would be noise). The parent is responsible for honouring
 *  that - pass ``visible.length <= 1 ? null : <LocationSubsetPicker .../>``. */
export function LocationSubsetPicker({ value, onChange, disabled }: LocationSubsetPickerProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const popoverRef = useFocusTrap<HTMLDivElement>(open, () => setOpen(false));

  const visible = useQuery<VisibleLocationsResponse>({
    queryKey: ["pos", "visible-locations"],
    queryFn: () => getPosClient().listVisibleLocations(),
    staleTime: 5 * 60_000,
  });

  // Click-outside dismissal - without this the popover stays open
  // when the operator clicks the underlying date input or a different
  // page section, which reads as broken.
  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent): void {
      const target = e.target as Node | null;
      if (!target) return;
      if (popoverRef.current?.contains(target)) return;
      if (buttonRef.current?.contains(target)) return;
      setOpen(false);
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open, popoverRef]);

  // Stable identity so dependent useMemos don't recompute every
  // render - the ?? [] fallback otherwise creates a new array each
  // call and tanks downstream memoisation.
  const locations: ReadonlyArray<VisibleLocation> = useMemo(
    () => visible.data?.locations ?? [],
    [visible.data],
  );
  const grouped = useMemo(() => groupByOrg(locations), [locations]);
  const selectedSet = useMemo(() => new Set(value), [value]);
  const summary = useMemo(() => {
    if (value.length === 0) {
      return t("scope.locationSubset.allLabel", {
        count: locations.length,
        defaultValue: "All {{count}} locations",
      });
    }
    if (value.length === 1) {
      const match = locations.find((l) => l.id === value[0]);
      return match ? match.name : t("scope.locationSubset.oneLabel");
    }
    return t("scope.locationSubset.nLabel", {
      count: value.length,
      defaultValue: "{{count}} locations",
    });
  }, [value, locations, t]);

  function toggle(locationId: string): void {
    const next = new Set(selectedSet);
    if (next.has(locationId)) {
      next.delete(locationId);
    } else {
      next.add(locationId);
    }
    onChange(Array.from(next));
  }

  function selectAll(orgId: string): void {
    const orgLocations = grouped.find((g) => g.orgId === orgId)?.locations ?? [];
    const next = new Set(selectedSet);
    const allSelected = orgLocations.every((l) => next.has(l.id));
    if (allSelected) {
      orgLocations.forEach((l) => next.delete(l.id));
    } else {
      orgLocations.forEach((l) => next.add(l.id));
    }
    onChange(Array.from(next));
  }

  function clear(): void {
    onChange([]);
  }

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        disabled={disabled}
        aria-haspopup="dialog"
        aria-expanded={open}
        className={cn(
          "inline-flex min-h-tap items-center gap-2 rounded-md bg-surface-muted px-3 text-sm font-medium text-foreground ring-1 ring-inset ring-line transition",
          "hover:bg-surface focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary",
          disabled && "cursor-not-allowed opacity-50",
        )}
      >
        <MapPin className="h-4 w-4 text-muted" aria-hidden="true" />
        <span>{summary}</span>
        <ChevronDown
          className={cn("h-4 w-4 text-muted transition-transform", open && "rotate-180")}
          aria-hidden="true"
        />
      </button>

      {open && (
        <div
          ref={popoverRef}
          role="dialog"
          aria-label={t("scope.locationSubset.dialogLabel")}
          className="absolute right-0 z-20 mt-2 max-h-[60vh] w-80 overflow-hidden rounded-xl bg-card shadow-elev-3 ring-1 ring-line"
        >
          <div className="flex items-center justify-between border-b border-line px-4 py-3">
            <p className="text-[0.6875rem] font-semibold uppercase tracking-wider text-muted">
              {t("scope.locationSubset.heading")}
            </p>
            <button
              type="button"
              onClick={clear}
              disabled={value.length === 0}
              className={cn(
                "text-xs font-medium",
                value.length === 0
                  ? "cursor-not-allowed text-faint"
                  : "text-brand-primary hover:underline",
              )}
            >
              {t("scope.locationSubset.consolidate")}
            </button>
          </div>

          <div className="max-h-[48vh] overflow-y-auto">
            {visible.isPending ? (
              <p className="px-4 py-6 text-sm text-muted">{t("common.loading")}</p>
            ) : grouped.length === 0 ? (
              <p className="px-4 py-6 text-sm text-muted">
                {t("scope.locationSubset.empty", {
                  defaultValue: "No locations are visible to this membership.",
                })}
              </p>
            ) : (
              grouped.map((g) => {
                const orgChecked = g.locations.every((l) => selectedSet.has(l.id));
                const orgPartial = !orgChecked && g.locations.some((l) => selectedSet.has(l.id));
                return (
                  <div key={g.orgId} className="border-b border-line/60 last:border-b-0">
                    <button
                      type="button"
                      onClick={() => selectAll(g.orgId)}
                      className="flex w-full items-center justify-between gap-2 bg-surface-muted/50 px-4 py-2 text-left text-xs font-semibold text-strong hover:bg-surface-muted"
                    >
                      <span className="inline-flex items-center gap-2">
                        <Building2 className="h-3.5 w-3.5 text-muted" aria-hidden="true" />
                        {g.orgName}
                      </span>
                      <span
                        aria-hidden="true"
                        className={cn(
                          "inline-flex h-4 w-4 items-center justify-center rounded border",
                          orgChecked
                            ? "border-brand-primary bg-brand-primary text-white"
                            : orgPartial
                              ? "border-brand-primary bg-brand-primary/20 text-brand-primary"
                              : "border-line bg-surface",
                        )}
                      >
                        {orgChecked && <Check className="h-3 w-3" />}
                        {orgPartial && <span className="block h-0.5 w-2 bg-brand-primary" />}
                      </span>
                    </button>
                    <ul>
                      {g.locations.map((l) => {
                        const checked = selectedSet.has(l.id);
                        return (
                          <li key={l.id}>
                            <button
                              type="button"
                              onClick={() => toggle(l.id)}
                              className="flex w-full items-center justify-between gap-2 px-4 py-2 text-left text-sm text-foreground hover:bg-surface-muted/60"
                            >
                              <span>{l.name}</span>
                              <span
                                aria-hidden="true"
                                className={cn(
                                  "inline-flex h-4 w-4 items-center justify-center rounded border",
                                  checked
                                    ? "border-brand-primary bg-brand-primary text-white"
                                    : "border-line bg-surface",
                                )}
                              >
                                {checked && <Check className="h-3 w-3" />}
                              </span>
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}

interface OrgGroup {
  readonly orgId: string;
  readonly orgName: string;
  readonly locations: ReadonlyArray<VisibleLocation>;
}

function groupByOrg(locations: ReadonlyArray<VisibleLocation>): ReadonlyArray<OrgGroup> {
  const map = new Map<string, OrgGroup & { locations: VisibleLocation[] }>();
  for (const l of locations) {
    const key = l.organization_id;
    const existing = map.get(key);
    if (existing) {
      existing.locations.push(l);
    } else {
      map.set(key, {
        orgId: l.organization_id,
        orgName: l.organization_name,
        locations: [l],
      });
    }
  }
  return Array.from(map.values());
}
